import { isResource } from '@medplum/core'
import type {
  Patient,
  QuestionnaireResponse,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import type { JsonObject } from '#src/json.ts'
import { ExtendedQuestionnaireResponse } from '#src/models/questionnaireResponse.ts'
import { VALIDATED_TOOL_TAG } from '#src/models/questionnaires/index.ts'
import { getValidatedToolComputation } from '#src/models/questionnaires/utils/validatedToolUtils.ts'
import type { ValidatedTool } from '#src/models/validatedTool.ts'
import { buildInitialResponse } from '#src/questionnaire.ts'
import type { QueryTypes } from '#src/search.ts'
import { ORDINAL_VALUE_EXTENSION_URL } from '#src/structureDefinitions.ts'
import { hasTag } from '#src/tags.ts'
import { isOfType } from '#src/utils.ts'

export class ValidatedToolResponse extends ExtendedQuestionnaireResponse {
  static isInstance(value: unknown): value is QuestionnaireResponse {
    return (
      isResource(value) &&
      isOfType(value, 'QuestionnaireResponse') &&
      hasTag(value, VALIDATED_TOOL_TAG)
    )
  }

  get displayText(): string | undefined {
    const computation = getValidatedToolComputation(this.toFhirResource())
    if (computation) {
      if (computation.code === 'time-in-seconds' && computation.score != null) {
        return `${computation.score}s`
      }
      if (computation.score != null) {
        return `${computation.score}`
      }
    }
    const score = this.score
    if (score > 0) {
      return `${score}`
    }
    // Score of 0 is valid for scored tools, so check if any items have ordinal values
    if (
      this.flatItems.some((item) =>
        item.answer?.some((a) => {
          const ext = a.extension ?? a.valueCoding?.extension
          return ext?.some(
            (e) =>
              e.url === ORDINAL_VALUE_EXTENSION_URL ||
              e.url === 'https://hl7.org/fhir/StructureDefinition/ordinalValue'
          )
        })
      )
    ) {
      return `${score}`
    }
    return undefined
  }

  get score(): number {
    const score = this.flatItems.reduce((acc, item) => {
      // Skip the calculated score fields to prevent double-counting
      // PHQ-2: /55758-7, PHQ-9: /44261-6, GAD7: /70274-6, STEADI: /97885-8, MoCA: /72133-2, /moca-mis
      if (
        item.linkId === '/44261-6' ||
        item.linkId === '/55758-7' ||
        item.linkId === '/70274-6' ||
        item.linkId === '/97885-8' ||
        item.linkId === '/72133-2' ||
        item.linkId === '/moca-mis'
      ) {
        return acc
      }

      const scores = compact(
        item.answer?.map((a) => {
          // TODO: remove the valueCoding check once all validated tools are updated
          const extension = a.extension ?? a.valueCoding?.extension
          // TODO: remove the 'https://hl7.org/fhir/StructureDefinition/ordinalValue' backwards compatibility check once all validated tool reponses are updated.
          const ordinalValue = extension?.find(
            (e) =>
              e.url === ORDINAL_VALUE_EXTENSION_URL ||
              e.url === 'https://hl7.org/fhir/StructureDefinition/ordinalValue'
          )
          return (
            ordinalValue?.valueDecimal ??
            ordinalValue?.valueInteger ??
            a.valueDecimal ??
            a.valueInteger ??
            0
          )
        })
      )
      return acc + scores.reduce((acc, score) => acc + score, 0)
    }, 0)
    return score
  }

  static byParticipant({
    participant,
  }: {
    participant: Reference<Patient>
  }): QueryTypes {
    return {
      patient: participant,
      _tag: VALIDATED_TOOL_TAG,
      _sort: '-_lastUpdated',
      _count: '100',
      'status:not': 'entered-in-error',
    }
  }

  static build(
    validatedTool: ValidatedTool,
    {
      valuesByLinkId,
    }: {
      valuesByLinkId?: JsonObject
    } = {}
  ): ValidatedToolResponse {
    const initialResponse = buildInitialResponse(validatedTool.toFhirResource())
    const resource: Omit<QuestionnaireResponse, 'resourceType'> = {
      ...initialResponse,
      questionnaire: validatedTool.canonical,
    }
    if (validatedTool.tag) {
      resource.meta = {
        tag: validatedTool.tag, // TODO: merge with existing tags
      }
    }
    const response = new ValidatedToolResponse(resource)
    Object.entries(valuesByLinkId ?? {}).forEach(([linkId, value]) => {
      response.setAnswer(validatedTool.getItem(linkId), value)
    })
    response.name = validatedTool.name
    return response
  }

  static buildSafe(
    validatedTool: ValidatedTool,
    {
      valuesByLinkId,
    }: {
      valuesByLinkId?: JsonObject
    } = {}
  ): ValidatedToolResponse {
    const initialResponse = buildInitialResponse(validatedTool.toFhirResource())
    const resource: Omit<QuestionnaireResponse, 'resourceType'> = {
      ...initialResponse,
      questionnaire: validatedTool.canonical,
    }
    if (validatedTool.tag) {
      resource.meta = {
        tag: validatedTool.tag, // TODO: merge with existing tags
      }
    }
    const response = new ValidatedToolResponse(resource)
    Object.entries(valuesByLinkId ?? {}).forEach(([linkId, value]) => {
      try {
        response.setAnswer(validatedTool.getItem(linkId), value)
      } catch (e) {
        console.warn(
          `Failed to set answer for ${linkId} on ${validatedTool.name}`,
          e
        )
      }
    })
    response.name = validatedTool.name
    return response
  }
}
